import React, { useContext, useEffect, useState, useMemo } from 'react';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import Loader from '../../../../../Components/Loader';

import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import PlatformCompanionConfig from './PlatformCompanionConfig';
import { initialCompanionAdConfig } from '../../../../../utilities/initialConfigs';
import {
	hasConfigKeys,
	shouldShowTgmrBackedConfig
} from '../../../../../utilities/tgmrSectionAdsHelpers';
import constants from '../../FeaturesDataConfig/constants.json';
import PageHeading from '../../../../../Components/PageHeading';
//mui imports====
import commonStyles from '../../../../../commonStyles';
import { Grid, Paper } from '@mui/material';
import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
//===============

const {
	platforms: { DESKTOP, MOBILE, TABLET },
	tgmrConstants: { PLATFORM_DESKTOP, PLATFORM_MOBILE, AP_BANNERSECTIONID }
} = constants;

const CompanionAdsConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = commonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext); //rawConfig from ConfigContext
	const { tgmrDoc, updateTgmrDoc } = useContext(UnitsContext);
	const [selectedPlatform, setSelectedPlatform] = useState(DESKTOP);
	const [docFetchError, setDocFetchError] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	let videoSectionId = rawConfig['videoSectionId'];
	const adsDoc = useMemo(() => {
		let ads = tgmrDoc?.ads?.filter(
			(ad) => ad?.framerateCompanionAd?.framerateSectionId === videoSectionId
		);
		return ads || [];
	}, [tgmrDoc, videoSectionId]);
	const hasAds = adsDoc.length > 0;
	const hasFeatureConfig = hasConfigKeys(rawConfig, keyData);
	const shouldShowConfigUi = shouldShowTgmrBackedConfig({ hasAds, hasFeatureConfig });

	// Fetching deskop Ap Tag Ad object from adDoc based upon the condition
	const desktopAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return adDoc?.formatData?.platform === PLATFORM_DESKTOP;
			});

			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// Fetching mobile Ap Tag Ad object from adDoc based upon the condition
	const mobileAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return adDoc?.formatData?.platform === PLATFORM_MOBILE;
			});
			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// The useEffect will be called when the component is rendering initially and when tgmrDoc gets updated, it fetches the tgmr doc from the api
	useEffect(() => {
		if (!tgmrDoc) {
			setIsLoading(true);
			updateTgmrDoc().finally(() => setIsLoading(false));
		} else {
			setIsLoading(false);
		}
	}, [tgmrDoc]);

	useEffect(() => {
		let error = '';
		let clonedRawConfig = structuredClone(rawConfig);

		// Check if adsDoc is present and companion ad configuration is not present in rawConfig then set default companion ad configuration
		if (hasAds && !hasFeatureConfig) {
			clonedRawConfig = { ...clonedRawConfig, ...initialCompanionAdConfig };
		}

		// Updating ApTag sectionIds for different platforms to cloned raw config
		updateApTagsIds(selectedPlatform, clonedRawConfig);

		// Check if tgmrDoc is present for the current site Id else update error
		if (!shouldShowConfigUi) {
			error = 'Please create an ApTag to enable Companion Ads';
		}
		setDocFetchError(error);
	}, [tgmrDoc, videoSectionId, hasAds, hasFeatureConfig, shouldShowConfigUi]);

	/**
	 * @name onPlatformChange
	 * @description The function is used to update the platform
	 * @param {string} newValue - It is the platform selected by user
	 * @param {function} event - It is the event being triggered when we update platform
	 */
	const onPlatformChange = (event, newValue) => {
		setSelectedPlatform(newValue);
		if (hasAds) {
			updateApTagsIds(newValue, structuredClone(rawConfig));
		}
	};

	/**
	 * @name updateApTagsIds
	 * @description The function is used to update the ap section Id for various platforms based upon the parameter in the object.
	 * @param {string} currentPlatform - It is the current platform for which section Id is to be updated
	 * @param {object} clonedRawConfig - It is the cloned raw config in which sectionIds will be appended.
	 */
	const updateApTagsIds = (currentPlatform, clonedRawConfig) => {
		if (currentPlatform === MOBILE || currentPlatform === TABLET) {
			// Check if mobile Ap Tag object is present in tgmr doc and id key is also present
			if (mobileAdObj && mobileAdObj.id) {
				const mobileKey = keyData + '.' + currentPlatform + '.' + AP_BANNERSECTIONID;
				clonedRawConfig[mobileKey] = mobileAdObj.id;
			}
		}
		// Check if the platform is DESKTOP, desktop Ap Tag object is present and id key inside the Ap Tag object must be present
		if (currentPlatform === DESKTOP && desktopAdObj && desktopAdObj.id) {
			const desktopKey = keyData + '.' + currentPlatform + '.' + AP_BANNERSECTIONID;
			clonedRawConfig[desktopKey] = desktopAdObj.id;
		}
		updateRawConfig(clonedRawConfig);
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			{isLoading ? (
				<Loader label="Loading Companion Ads Config..." />
			) : !shouldShowConfigUi ? (
				<Box
					sx={{
						marginTop: '4rem',
						paddingBottom: '15rem',
						textAlign: 'center',
						color: 'red',
						fontWeight: 'bold',
						fontSize: '2rem'
					}}
				>
					{docFetchError ||
						'No Companion Ads Config found. Please create an ApTag to enable Companion Ads.'}
				</Box>
			) : (
				<>
					<PageHeading
						headingName="Companion Ads"
						headingPath={['Dashboard', 'Framerate', 'Network Data', 'Companion Ads']}
						description="Manage Platform Level Companion Ads here"
						showInfoButton={true}
						infoMessage={featureDescription}
					/>

					<Grid
						container
						spacing={0}
						style={{ marginLeft: '5rem', marginTop: '4rem', width: '90%' }}
					>
						<Grid item xs={6}>
							<CheckBoxInput
								keyData={keyData + '.enabled'}
								label="enabled"
								disabled={false}
								defaultChecked={false}
								object={rawConfig}
								updateObject={updateRawConfig}
								containerStyles={{ width: 'fit-content' }}
							/>
						</Grid>
					</Grid>

					<TabContext value={selectedPlatform}>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center'
							}}
						>
							<TabList onChange={onPlatformChange}>
								<Tab label={DESKTOP} value={DESKTOP} />
								<Tab label={MOBILE} value={MOBILE} />
								<Tab label={TABLET} value={TABLET} />
							</TabList>
						</Box>
						<TabPanel value={DESKTOP}>
							<PlatformCompanionConfig
								keyData={keyData + '.' + DESKTOP}
								platform={DESKTOP}
								desktopAdObj={desktopAdObj}
							/>
						</TabPanel>
						<TabPanel value={MOBILE}>
							<PlatformCompanionConfig
								keyData={keyData + '.' + MOBILE}
								platform={MOBILE}
								mobileAdObj={mobileAdObj}
							/>
						</TabPanel>
						<TabPanel value={TABLET}>
							<PlatformCompanionConfig
								keyData={keyData + '.' + TABLET}
								platform={TABLET}
								mobileAdObj={mobileAdObj}
							/>
						</TabPanel>
					</TabContext>
				</>
			)}
		</Paper>
	);
};

export default CompanionAdsConfig;
